import { PlusIcon } from "@primer/octicons-react";
import * as React from "react";
import { useEffect, useState } from "react";
import Api from "../../api";
import { useCredits } from "../../credits";
import { Text, User } from "../../models";
import TextCard from "./TextCard";
import TextForm from "./TextForm";

const TextsPage: React.FC<{ user: User | null }> = ({ user }) => {
  const [showForm, setShowForm] = useState<boolean>(false);
  const [texts, setTexts] = useState<Text[]>([]);
  // const [error, setError] = useState<string>("");

  const { credits, syncCredits } = useCredits();

  const addText: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    setShowForm(true);
  };

  function handleTextCreated(text: Text) {
    setTexts((texts) => [text, ...texts]);
    setShowForm(false);
  }

  async function loadTexts() {
    try {
      const res = await Api.texts.list();

      if ("errors" in res) {
        // setError(res.errors.join(", "));
        console.error("Error ocurred while loading texts: ", res.errors);
        return;
      }

      setTexts(res);
      if (res.length === 0) {
        setShowForm(true);
      }
    } catch (err) {
      console.error("Error ocurred while loading texts: ", err);
      return;
    }
  }

  const handleStart = async (uid: string) => {
    const t = texts.find((t) => t.uid === uid);
    if (!t) {
      return;
    }

    if (!user) {
      window.location.href = `/users/sign_up/?assign=${t.uid}`;
    }

    const newText = await Api.texts.enqueue(t.uid);
    if ("errors" in newText) {
      const e = newText.errors.join(", ");
      setTexts((texts) =>
        texts.map((t) =>
          t.uid === uid
            ? {
                ...t,
                _error: e,
              }
            : t,
        ),
      );
      return;
    }

    setTexts((texts) =>
      texts.map((t) => (t.uid === newText.uid ? newText : t)),
    );
    syncCredits();
  };

  useEffect(() => {
    loadTexts();
  }, []);

  const hasActiveJobs = texts.some(
    (t) => t.status === "enqueued" || t.status === "running",
  );

  const outOfCredits = !!user && credits === 0;

  useEffect(() => {
    if (!user || !hasActiveJobs) {
      return;
    }
    const interval = setInterval(() => {
      loadTexts();
    }, 3000);
    return () => clearInterval(interval);
  }, [user, hasActiveJobs]);

  return (
    <main className="home-page">
      <div className="container">
        {user && credits !== null && (
          <div className="credits-row">
            <a
              href="/settings"
              className="credits-chip"
              title="Credits remaining"
            >
              <span className="credits-chip-dot" />
              <span className="credits-chip-value">{credits}</span>
              <span className="credits-chip-label">credits left</span>
            </a>
          </div>
        )}

        {outOfCredits && (
          <div className="no-credits-banner">
            <div className="no-credits-banner__icon">✨</div>
            <div className="no-credits-banner__body">
              <h3>You're out of credits</h3>
              <p>Grab a credit pack to keep generating new card sets.</p>
            </div>
            <a href="/settings" className="no-credits-banner__cta">
              Buy credits
            </a>
          </div>
        )}

        {showForm && !outOfCredits && (
          <TextForm onTextCreated={handleTextCreated} />
        )}

        <div className="text-list">
          {user && !showForm && !outOfCredits && (
            <a href="#" className="add-text" onClick={addText}>
              <PlusIcon size={32} /> NEW
            </a>
          )}
          {texts.length > 0 &&
            texts.map((t) => (
              <TextCard
                key={t.uid}
                url={`/texts/${t.uid}`}
                text={t}
                onStart={() => handleStart(t.uid)}
              />
            ))}
        </div>
      </div>
    </main>
  );
};

export default TextsPage;
